/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */
"use client";
import React, { useState } from "react";
import Image from "next/image";
import { Link } from "@heroui/link";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@heroui/dropdown";
import { useRouter } from "next/navigation";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

interface NonAuthFooterProps {
  footerData: any;
  csrfToken: any;
}

interface SiblingSiteSelectorProps {
  currentSiblingSite: any;
  siblingSites: any;
  router: AppRouterInstance;
}

const SiblingSiteSelector: React.FC<SiblingSiteSelectorProps> = ({
  currentSiblingSite,
  siblingSites,
  router,
}) => {
  return (
    <Dropdown>
      <DropdownTrigger>
        <div className="flex items-center justify-between w-fit gap-2 cursor-pointer">
          <Image
            alt={currentSiblingSite.icon.title}
            className="w-5 h-5 object-contain rounded-full"
            height={20}
            src={currentSiblingSite.icon.meta.download_url}
            width={20}
          />
          <p>{currentSiblingSite.title}</p>
          <Image
            alt="Down Arrow"
            className="w-6 h-6 object-contain"
            height={24}
            src="/icons/arrow_down_white.svg"
            width={24}
          />
        </div>
      </DropdownTrigger>
      <DropdownMenu>
        {siblingSites
          .filter((site: any) => !site.is_current_site)
          .map((site: any, index: number) => (
            <DropdownItem
              key={index}
              onPress={() => {
                router.push(site.link);
              }}
            >
              <div className="flex items-center justify-between w-fit gap-4">
                <Image
                  alt={site.icon.title}
                  className="w-5 h-5 object-contain rounded-full"
                  height={20}
                  src={site.icon.meta.download_url}
                  width={20}
                />
                <p>{site.title}</p>
              </div>
            </DropdownItem>
          ))}
      </DropdownMenu>
    </Dropdown>
  );
};

const NonAuthFooter: React.FC<NonAuthFooterProps> = ({
  footerData,
  csrfToken,
}) => {
  const formattedFooterData = footerData.items[0];
  const router = useRouter();

  const [currentSiblingSite, setCurrentSiblingSite] = useState(
    formattedFooterData.sibling_sites.filter(
      (site: any) => site.is_current_site,
    )[0],
  );

  return (
    <div className="w-full bg-ax-tertiary">
      <div className="flex flex-col w-full mx-auto px-4 md:px-14 xl:px-[100px] py-10 md:py-5 max-w-[1440px]">
        <div className="flex flex-col md:grid grid-cols-2 lg:flex lg:flex-row gap-10 justify-between mt-5 md:mt-10 text-white/80">
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-10 justify-between">
              <Image
                alt={formattedFooterData.footer_logo.title}
                className="max-w-40 md:max-w-48 lg:max-w-52 h-auto w-auto"
                height={100}
                src={formattedFooterData.footer_logo.meta.download_url}
                width={200}
              />
              <div className="md:hidden flex">
                <SiblingSiteSelector
                  currentSiblingSite={currentSiblingSite}
                  router={router}
                  siblingSites={formattedFooterData.sibling_sites}
                />
              </div>
            </div>
            <p className="text-sm max-w-[350px]">
              {formattedFooterData.footer_logo_text}
            </p>
            <Link
              className="text-white text-2xl font-medium hidden md:block"
              href={`tel:${formattedFooterData.contact_number}`}
            >
              {formattedFooterData.contact_number}
            </Link>
            <div className="flex gap-2 items-center justify-start">
              {formattedFooterData.footer_social_links.map(
                (socialLink: any, index: number) => (
                  <Link key={index} href={socialLink.link}>
                    <Image
                      alt={socialLink.icon.title}
                      className="w-8 h-8 object-contain"
                      height={32}
                      src={socialLink.icon.meta.download_url}
                      width={32}
                    />
                  </Link>
                ),
              )}
            </div>
          </div>
          <div className="flex flex-row flex-wrap gap-10 md:gap-y-10 lg:gap-20 md:gap-x-40">
            {formattedFooterData.footer_link_items.map(
              (linkItem: any, index: number) => (
                <div key={index} className="flex flex-col gap-4">
                  <p className="text-white font-medium">
                    {linkItem.value.title}
                  </p>
                  <div className="flex flex-col gap-3">
                    {linkItem.value.link_items.map(
                      (link: any, index: number) => (
                        <Link
                          key={index}
                          className="text-white/80 hover:text-white"
                          href={link.value.link}
                        >
                          {link.value.title}
                        </Link>
                      ),
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
          <div className="flex flex-col gap-5">
            <div className="hidden md:flex">
              <SiblingSiteSelector
                currentSiblingSite={currentSiblingSite}
                router={router}
                siblingSites={formattedFooterData.sibling_sites}
              />
            </div>
            <div className="flex flex-col gap-4">
              <p className="text-white font-medium">Contact Us</p>
              <div>
                {formattedFooterData.address
                  .split("\\n")
                  .map((line: string, index: number) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
              </div>
              <Link
                className="md:hidden block"
                href={`tel:${formattedFooterData.contact_number}`}
              >
                {formattedFooterData.contact_number}
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              <p className="text-white font-medium">Download Our App</p>
              <div className="flex gap-6 items-center justify-start">
                {formattedFooterData.app_links.map(
                  (appLink: any, index: number) => (
                    <Link key={index} href={appLink.link}>
                      <img
                        alt={appLink.icon.title}
                        className="max-w-40 md:max-w-48 lg:max-w-52"
                        height={32}
                        src={appLink.icon.meta.download_url}
                        width={32}
                      />
                    </Link>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full h-[1px] bg-white/20 mt-8 mb-6" />
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between text-white/80 text-sm">
          <p>{formattedFooterData.copyright_text}</p>
          <p>
            Designed and Developed By{" "}
            <span>
              <Link className="text-white underline" href="https://quixta.in">
                Quixta
              </Link>
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default NonAuthFooter;
